import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Home, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, verify2FA, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("creds"); // creds | 2fa
  const [challengeId, setChallengeId] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [code, setCode] = useState("");

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.requires_2fa) {
        setChallengeId(data.challenge_id);
        setPhoneHint(data.phone_hint || "");
        setStep("2fa");
        toast.success("Code sent to your phone!");
      } else if (data.twofa_skipped) {
        toast.warning("SMS service unavailable — logged in without 2FA");
        nav("/");
      } else {
        toast.success("Welcome back!");
        nav("/");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2FA(challengeId, code.trim());
      toast.success("Welcome back!");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--hh-cream)" }}>
      <div className="w-full max-w-md nb-card p-9 hh-fade-up">
        <div className="flex items-center gap-3 mb-7">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: "var(--hh-terracotta)", color: "var(--hh-card)" }}
          >
            {step === "2fa" ? <ShieldCheck strokeWidth={2.2} /> : <Home strokeWidth={2.2} />}
          </div>
          <div>
            <h1 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>familytable</h1>
            <p className="text-xs uppercase tracking-wider" style={{ color: "var(--hh-ink-soft)" }}>{step === "2fa" ? "Verify identity" : "Welcome back"}</p>
          </div>
        </div>

        {step === "creds" ? (
          <>
            <h2 className="text-4xl font-extrabold mb-1 leading-tight" style={{ fontFamily: "var(--hh-display)" }}>Sign in</h2>
            <p className="text-sm mb-7" style={{ color: "var(--hh-ink-soft)" }}>to your family table.</p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: "var(--hh-ink-soft)" }}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="nb-input w-full" data-testid="login-email" placeholder="you@family.com" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: "var(--hh-ink-soft)" }}>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="nb-input w-full" data-testid="login-password" />
              </div>
              <button type="submit" disabled={loading} className="nb-btn nb-btn-ink w-full py-3 text-base mt-2" data-testid="login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            <p className="text-sm text-center mt-7" style={{ color: "var(--hh-ink-soft)" }}>
              New family?{" "}
              <Link to="/register" className="font-semibold" style={{ color: "var(--hh-terracotta)", borderBottom: "1px solid var(--hh-terracotta)" }} data-testid="link-register">
                Create your account
              </Link>
            </p>
          </>
        ) : (
          <>
            <h2 className="text-4xl font-extrabold mb-1 leading-tight" style={{ fontFamily: "var(--hh-display)" }}>Enter code</h2>
            <p className="text-sm mb-7" style={{ color: "var(--hh-ink-soft)" }}>We texted a 6-digit code to {phoneHint || "your phone"}. Code expires in 5 minutes. You can also use a backup code.</p>

            <form onSubmit={verifyCode} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-2 block" style={{ color: "var(--hh-ink-soft)" }}>Verification code</label>
                <input
                  inputMode="text"
                  maxLength={20}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  className="nb-input w-full text-center text-xl tracking-[0.4em] uppercase"
                  style={{ fontFamily: "var(--hh-display)", fontWeight: 600 }}
                  data-testid="login-2fa-code"
                  placeholder="••••••"
                  autoFocus
                />
              </div>
              <button type="submit" disabled={loading || code.length < 6} className="nb-btn nb-btn-ink w-full py-3 text-base" data-testid="login-2fa-verify">
                {loading ? "Verifying…" : "Verify & Sign In"}
              </button>
              <button type="button" onClick={() => { setStep("creds"); setCode(""); }} className="text-sm w-full text-center" style={{ color: "var(--hh-ink-soft)" }} data-testid="login-2fa-back">
                ← Back to login
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
